/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { SelectControl } from '@wordpress/components';
import { chevronLeft, chevronRight } from '@wordpress/icons';

/**
 * Pagination component for event lists
 *
 * @param {Object}   props
 * @param {number}   props.currentPage         Current page number (1-based).
 * @param {number}   props.totalPages          Total number of pages.
 * @param {number}   props.totalItems          Total number of items.
 * @param {number}   props.itemsPerPage        Items per page.
 * @param {Function} props.onPageChange        Callback for page changes.
 * @param {Function} props.onItemsPerPageChange Callback for page size changes.
 * @param {boolean}  props.showItemsPerPage    Show items per page selector.
 * @param {boolean}  props.showInfo            Show pagination info text.
 * @param {boolean}  props.disabled            Disable all controls.
 * @param {Object}   props.config              Configuration options.
 * @return {JSX.Element} The Pagination component.
 */
const Pagination = ({
    currentPage = 1,
    totalPages = 1,
    totalItems = 0,
    itemsPerPage = 10,
    onPageChange = () => { },
    onItemsPerPageChange = () => { },
    showItemsPerPage = true,
    showInfo = true,
    disabled = false,
    config = {}
}) => {
    // Default configuration
    const defaultConfig = {
        maxVisiblePages: 7,
        pageSizeOptions: [10, 25, 50, 100],
        strings: {
            info: __('Showing {start}-{end} of {total} items', 'nemtly-booking'),
            itemsPerPage: __('Items per page', 'nemtly-booking'),
            first: __('First', 'nemtly-booking'),
            previous: __('Previous', 'nemtly-booking'),
            next: __('Next', 'nemtly-booking'),
            last: __('Last', 'nemtly-booking'),
            goToPage: __('Go to page {page}', 'nemtly-booking'),
            currentPage: __('Page {page}, current page', 'nemtly-booking')
        }
    };

    const finalConfig = { ...defaultConfig, ...config };

    /**
     * Calculate pagination info
     */
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    /**
     * Generate page numbers with smart truncation
     *
     * @return {Array} Array of page numbers and ellipsis.
     */
    const generatePageNumbers = () => {
        const { maxVisiblePages } = finalConfig;
        const pages = [];

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is less than max visible
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Smart truncation logic
            const halfVisible = Math.floor(maxVisiblePages / 2);
            let startPage = Math.max(1, currentPage - halfVisible);
            let endPage = Math.min(totalPages, currentPage + halfVisible);

            // Adjust if we're near the beginning
            if (currentPage <= halfVisible) {
                endPage = Math.min(maxVisiblePages, totalPages);
            }

            // Adjust if we're near the end
            if (currentPage > totalPages - halfVisible) {
                startPage = Math.max(1, totalPages - maxVisiblePages + 1);
            }

            // Always show first page
            if (startPage > 1) {
                pages.push(1);
                if (startPage > 2) {
                    pages.push('...');
                }
            }

            // Add middle pages
            for (let i = startPage; i <= endPage; i++) {
                if (i !== 1 && i !== totalPages) {
                    pages.push(i);
                }
            }

            // Always show last page
            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pages.push('...');
                }
                pages.push(totalPages);
            }
        }

        return pages;
    };

    /**
     * Handle page change
     *
     * @param {number} page Target page number.
     */
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage && !disabled) {
            onPageChange(page);
        }
    };

    /**
     * Handle items per page change
     *
     * @param {string} value New items per page value.
     */
    const handleItemsPerPageChange = (value) => {
        if (!disabled) {
            onItemsPerPageChange(parseInt(value, 10));
        }
    };

    // Don't render if no pagination needed
    if (totalPages <= 1 && !showInfo && !showItemsPerPage) {
        return null;
    }

    const pageNumbers = generatePageNumbers();

    return (
        <div className="nb-pagination-container">
            {/* Mobile pagination info */}
            <div className="nb-pagination-info">
                {showInfo && (
                    <p className="nb-text-sm nb-text-gray-700">
                        {finalConfig.strings.info
                            .replace('{start}', startItem)
                            .replace('{end}', endItem)
                            .replace('{total}', totalItems)}
                    </p>
                )}

                {showItemsPerPage && (
                    <div className="nb-flex nb-items-center nb-space-x-2">
                        <label htmlFor="items-per-page" className="nb-text-sm nb-text-gray-700">
                            {finalConfig.strings.itemsPerPage}:
                        </label>
                        <SelectControl
                            id="items-per-page"
                            value={itemsPerPage}
                            options={finalConfig.pageSizeOptions.map(size => ({
                                label: size.toString(),
                                value: size
                            }))}
                            onChange={handleItemsPerPageChange}
                            disabled={disabled}
                            aria-label={finalConfig.strings.itemsPerPage}
                            __next40pxDefaultSize
                            __nextHasNoMarginBottom
                        />
                    </div>
                )}
            </div>

            {/* Desktop pagination controls */}
            <div className="nb-pagination-controls">
                <div className="nb-flex nb-items-center">
                    {showInfo && (
                        <p className="nb-text-sm nb-text-gray-700">
                            {finalConfig.strings.info
                                .replace('{start}', startItem)
                                .replace('{end}', endItem)
                                .replace('{total}', totalItems)}
                        </p>
                    )}
                </div>

                <div className="nb-flex nb-items-center nb-space-x-2">
                    {showItemsPerPage && (
                        <div className="nb-flex nb-items-center nb-space-x-2 nb-mr-4">
                            <label htmlFor="items-per-page-desktop" className="nb-text-sm nb-text-gray-700">
                                {finalConfig.strings.itemsPerPage}:
                            </label>
                            <SelectControl
                                id="items-per-page-desktop"
                                value={itemsPerPage}
                                options={finalConfig.pageSizeOptions.map(size => ({
                                    label: size.toString(),
                                    value: size
                                }))}
                                onChange={handleItemsPerPageChange}
                                disabled={disabled}
                                aria-label={finalConfig.strings.itemsPerPage}
                                __next40pxDefaultSize
                                __nextHasNoMarginBottom
                            />
                        </div>
                    )}

                    <nav
                        className="nb-flex nb-items-center nb-gap-1"
                        aria-label={__('Events pagination', 'nemtly-booking')}
                        role="navigation"
                    >
                        {/* First page button */}
                        {pageNumbers[0] !== 1 && (
                            <button
                                type="button"
                                className="nb-flex nb-items-center nb-gap-2 nb-px-4 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm disabled:nb-opacity-50 disabled:nb-cursor-not-allowed"
                                onClick={() => handlePageChange(1)}
                                disabled={disabled || currentPage === 1}
                                aria-label={finalConfig.strings.first}
                            >
                                <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                                <span className="nb-hidden sm:nb-inline">{finalConfig.strings.first}</span>
                            </button>
                        )}

                        {/* Previous page button */}
                        {currentPage > 1 && (
                            <button
                                type="button"
                                className="nb-flex nb-items-center nb-gap-2 nb-px-4 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm disabled:nb-opacity-50 disabled:nb-cursor-not-allowed"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={disabled || currentPage === 1}
                                aria-label={finalConfig.strings.previous}
                            >
                                <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className="nb-hidden sm:nb-inline">{finalConfig.strings.previous}</span>
                            </button>
                        )}

                        {/* Page number buttons */}
                        {pageNumbers.map((page, index) => {
                            if (page === '...') {
                                return (
                                    <span
                                        key={`ellipsis-${index}`}
                                        className="nb-px-2 nb-py-2.5 nb-text-slate-400 nb-font-medium"
                                    >
                                        ...
                                    </span>
                                );
                            }

                            const isCurrentPage = page === currentPage;
                            return (
                                <button
                                    key={page}
                                    type="button"
                                    className={`nb-px-3 nb-py-2.5 nb-rounded-lg nb-border nb-font-medium nb-transition-all nb-duration-200 nb-shadow-sm focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 disabled:nb-opacity-50 disabled:nb-cursor-not-allowed ${
                                        isCurrentPage 
                                            ? 'nb-bg-slate-600 nb-text-white nb-border-slate-600 nb-shadow-md' 
                                            : 'nb-border-slate-200 nb-bg-white nb-text-slate-700 hover:nb-bg-slate-50 hover:nb-border-slate-300'
                                    }`}
                                    onClick={() => handlePageChange(page)}
                                    disabled={disabled}
                                    aria-label={
                                        isCurrentPage
                                            ? finalConfig.strings.currentPage.replace('{page}', page)
                                            : finalConfig.strings.goToPage.replace('{page}', page)
                                    }
                                    aria-current={isCurrentPage ? "page" : undefined}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        {/* Next page button */}
                        {currentPage < totalPages && (
                            <button
                                type="button"
                                className="nb-flex nb-items-center nb-gap-2 nb-px-4 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm disabled:nb-opacity-50 disabled:nb-cursor-not-allowed"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={disabled || currentPage === totalPages}
                                aria-label={finalConfig.strings.next}
                            >
                                <span className="nb-hidden sm:nb-inline">{finalConfig.strings.next}</span>
                                <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}

                        {/* Last page button */}
                        {pageNumbers[pageNumbers.length - 1] !== totalPages && (
                            <button
                                type="button"
                                className="nb-flex nb-items-center nb-gap-2 nb-px-4 nb-py-2.5 nb-rounded-lg nb-border nb-border-slate-200 nb-bg-white nb-text-slate-700 nb-font-medium hover:nb-bg-slate-50 hover:nb-border-slate-300 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-slate-500 focus:nb-ring-offset-2 nb-transition-all nb-duration-200 nb-shadow-sm disabled:nb-opacity-50 disabled:nb-cursor-not-allowed"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={disabled || currentPage === totalPages}
                                aria-label={finalConfig.strings.last}
                            >
                                <span className="nb-hidden sm:nb-inline">{finalConfig.strings.last}</span>
                                <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7m-8 14l7-7-7-7" />
                                </svg>
                            </button>
                        )}
                    </nav>
                </div>
            </div>
        </div>
    );
};

export default Pagination; 